import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../helpers/utils';
import classes from './styles.module.scss';
import { apiService, MeetingListItem } from '../../services/api.service';
import { useTasks } from '../../contexts/tasks.context';
import { pad } from '../files/files.page';
import { getFileVisualType } from '../files/utils/file-type';
import FileTypeIcon from './components/file-type-icon.component';
import {
  AlertTriangleIcon,
  CircleCheckIcon,
  ChevronRightIcon,
  FilesIcon,
  ListCheckIcon,
  MicIcon,
  SpeakersIcon,
} from '../../assets';

const cn = createCn('overview', classes);

function formatApiDateLocal(dateStr: string): string {
  if (!dateStr) return '';
  if (dateStr.includes('.')) return dateStr;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  return `${pad(day)}.${pad(month)}.${year}`;
}

/** Просроченная задача: due_date < сегодня и статус не done (данные из GET /api/tasks). */
function isTaskOverdue(task: { due_date?: string | null; status: string }): boolean {
  if (!task.due_date || task.status === 'done') return false;
  const due = new Date(task.due_date);
  if (Number.isNaN(due.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}

function isMeetingInCurrentMonth(meeting: MeetingListItem): boolean {
  const now = new Date();
  if (meeting.created_at) {
    const d = new Date(meeting.created_at);
    if (!Number.isNaN(d.getTime())) {
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }
  }
  const [year, month] = (meeting.date || '').split('-').map(Number);
  if (year && month) {
    return month - 1 === now.getMonth() && year === now.getFullYear();
  }
  const [day, m, y] = (meeting.date || '').split('.').map(Number);
  if (y && m) {
    return m - 1 === now.getMonth() && y === now.getFullYear();
  }
  return false;
}

const OverviewPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tasks } = useTasks();
  const [meetings, setMeetings] = useState<MeetingListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiService.getMeetings()
      .then(data => {
        if (!cancelled) setMeetings(data);
      })
      .catch(() => {
        if (!cancelled) setMeetings([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const visibleMeetings = useMemo(
    () => meetings,
    [meetings],
  );

  const stats = useMemo(() => {
    const completed = visibleMeetings.filter(m => m.status === 'completed' || m.status === 'stored').length;
    const processing = visibleMeetings.filter(m => m.status === 'processing').length;
    const errors = visibleMeetings.filter(m => m.duration === '00:00:00' || m.status === 'error').length;
    const thisMonth = visibleMeetings.filter(isMeetingInCurrentMonth).length;

    const tasksCount = tasks.length;
    const overdueTasks = tasks.filter(isTaskOverdue).length;

    return {
      total: visibleMeetings.length,
      completed,
      processing,
      errors,
      thisMonth,
      tasksCount,
      overdueTasks,
    };
  }, [visibleMeetings, tasks]);

  const statCards = useMemo(() => [
    {
      key: 'meetings',
      label: t('overview.stat-meetings'),
      value: loading ? '—' : stats.total,
      hint: loading
        ? ''
        : stats.thisMonth > 0
          ? t('overview.hint-month', { count: stats.thisMonth })
          : t('overview.hint-no-month'),
      tone: 'neutral' as const,
      icon: <MicIcon />,
      iconTone: 'green',
    },
    {
      key: 'processed',
      label: t('overview.stat-processed'),
      value: loading ? '—' : stats.completed,
      hint: loading
        ? ''
        : stats.processing > 0
          ? t('overview.hint-processing', { count: stats.processing })
          : t('overview.hint-all-processed'),
      tone: 'success' as const,
      icon: <CircleCheckIcon />,
      iconTone: 'green',
    },
    {
      key: 'tasks',
      label: t('overview.stat-tasks'),
      value: stats.tasksCount,
      hint: stats.overdueTasks > 0
        ? t('overview.hint-overdue', { count: stats.overdueTasks })
        : t('overview.hint-tasks-active', { count: stats.tasksCount }),
      tone: stats.overdueTasks > 0 ? 'warning' as const : 'neutral' as const,
      icon: <ListCheckIcon />,
      iconTone: 'blue',
    },
    {
      key: 'errors',
      label: t('overview.stat-errors'),
      value: loading ? '—' : stats.errors,
      hint: loading
        ? ''
        : stats.errors > 0
          ? t('overview.hint-errors', { count: stats.errors })
          : t('overview.hint-no-errors'),
      tone: stats.errors > 0 ? 'error' as const : 'success' as const,
      icon: <AlertTriangleIcon />,
      iconTone: stats.errors > 0 ? 'amber' : 'green',
    },
  ], [loading, stats, t]);

  const recentMeetings = useMemo(() => {
    return [...visibleMeetings]
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .slice(0, 5);
  }, [visibleMeetings]);

  const quickLinks = [
    {
      label: t('navigation.files'),
      desc: t('overview.link-files'),
      to: '/files',
      icon: <FilesIcon />,
      tone: 'green',
    },
    {
      label: t('navigation.tasks'),
      desc: t('overview.link-tasks'),
      to: '/tasks',
      icon: <ListCheckIcon />,
      tone: 'blue',
    },
    {
      label: t('navigation.speakers'),
      desc: t('overview.link-speakers'),
      to: '/speakers',
      icon: <SpeakersIcon />,
      tone: 'amber',
    },
  ];

  const getMeetingStatus = (meeting: MeetingListItem) => {
    if (meeting.status === 'processing') {
      return { label: t('files.status-processing'), tone: 'processing' as const };
    }
    if (meeting.duration === '00:00:00' || meeting.status === 'error') {
      return { label: t('files.status-error'), tone: 'error' as const };
    }
    return { label: t('files.status-processed'), tone: 'processed' as const };
  };

  return (
    <div className={cn()}>
      <header className={cn('header')}>
        <div>
          <h1 className={cn('title')}>{t('overview.heading')}</h1>
          <p className={cn('subtitle')}>{t('overview.subtitle')}</p>
        </div>
      </header>

      <div className={cn('stats')}>
        {statCards.map(card => (
          <div key={card.key} className={cn('stat-card')}>
            <div className={cn('stat-top')}>
              <span className={cn('stat-label')}>{card.label}</span>
              <div className={cn('stat-icon', { [card.iconTone]: true })}>
                {card.icon}
              </div>
            </div>
            <strong className={cn('stat-value', { [card.tone]: true })}>{card.value}</strong>
            {card.hint && <span className={cn('stat-hint')}>{card.hint}</span>}
          </div>
        ))}
      </div>

      <div className={cn('grid')}>
        <section className={cn('panel')}>
          <div className={cn('panel-header')}>
            <h2>{t('overview.recent-title')}</h2>
            <button type="button" className={cn('panel-link')} onClick={() => navigate('/files')}>
              {t('overview.all-files')}
            </button>
          </div>
          {loading && <p className={cn('empty')}>{t('overview.loading')}</p>}
          {!loading && recentMeetings.length === 0 && (
            <p className={cn('empty')}>{t('overview.no-meetings')}</p>
          )}
          {!loading && recentMeetings.length > 0 && (
            <ul className={cn('recent-list')}>
              {recentMeetings.map(meeting => {
                const status = getMeetingStatus(meeting);
                const visualType = getFileVisualType(meeting.name);
                return (
                  <li
                    key={meeting.id}
                    className={cn('recent-item')}
                    onClick={() => navigate(`/files/${meeting.id}`)}
                  >
                    <FileTypeIcon type={visualType} />
                    <div className={cn('recent-main')}>
                      <span className={cn('recent-name')}>{meeting.name}</span>
                      <span className={cn('recent-meta')}>{formatApiDateLocal(meeting.date)}</span>
                    </div>
                    <div className={cn('recent-side')}>
                      <span className={cn('recent-status', { [status.tone]: true })}>
                        <span className={cn('status-dot', { [status.tone]: true })} />
                        {status.label}
                      </span>
                      <span className={cn('recent-duration')}>{meeting.duration || '—'}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className={cn('panel')}>
          <h2 className={cn('panel-title')}>{t('overview.quick-title')}</h2>
          <div className={cn('quick-links')}>
            {quickLinks.map(link => (
              <button
                key={link.to}
                type="button"
                className={cn('quick-link')}
                onClick={() => navigate(link.to)}
              >
                <div className={cn('quick-link-icon', { [link.tone]: true })}>
                  {link.icon}
                </div>
                <div className={cn('quick-link-text')}>
                  <span className={cn('quick-link-label')}>{link.label}</span>
                  <span className={cn('quick-link-desc')}>{link.desc}</span>
                </div>
                <ChevronRightIcon className={cn('quick-link-arrow')} />
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default OverviewPage;
